/**
 * LA TRÉSORERIE — lot 100, 18/09/2026.
 *
 * Maquette `maquette-analyse-tresorerie.html`, cadrage « Analyse financière :
 * 4. la trésorerie » et « 8. les arbitrages ». Tuiles, courbe probable /
 * prudent avec le minimum et le découvert, tableau semaine × nature dont
 * chaque case ouvre ses flux, passé (prévu le lundi / réalisé). Pas de
 * simulation « et si » (doc 8).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  fluxDeLaSemaine,
  NATURES,
  REGLES_DE_BASE,
  type FluxTresorerie,
  type Nature,
  type Scenario,
} from '@stafa/core';
import type { EchecApi } from './api.js';
import {
  ajouterRegleTresorerie,
  lireTresorerie,
  retirerRegleTresorerie,
} from './apiAnalyse.js';
import { dateCourte, dateFr, eurosCourts } from './bfr.js';
import { CourbeAnalyse } from './CourbeAnalyse.js';
import { BandeauFiabilite } from './BandeauFiabilite.js';
import { PanneauReglages } from './PanneauReglages.js';
import {
  eurosEntiers,
  eurosSignes,
  heureDeParis,
  NOM_NIVEAU,
  niveauDe,
  parNature,
  vueTresorerie,
  type RegleLue,
  type TresorerieLue,
} from './tresorerie.js';

const NOM_NATURE = new Map(NATURES.map((n) => [n.cle, n.nom]));
const nomNature = (n: Nature) => NOM_NATURE.get(n) ?? n;

export interface ActionsRegles {
  readonly ajouter: (motif: string, nature: Nature) => Promise<string | null>;
  readonly retirer: (id: string) => Promise<string | null>;
}

export function EcranTresorerie({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<TresorerieLue | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  async function charger() {
    const j = await jeton();
    if (j === null) return quandAccesPerdu();
    const r = await lireTresorerie(j);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause));
    }
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  const actions: ActionsRegles = {
    ajouter: async (motif, nature) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await ajouterRegleTresorerie(j, motif, nature);
      if (r.etat !== 'ok') return r.message ?? messageRefus(r.cause);
      await charger();
      return null;
    },
    retirer: async (id) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await retirerRegleTresorerie(j, id);
      if (r.etat !== 'ok') return r.message ?? messageRefus(r.cause);
      await charger();
      return null;
    },
  };

  return (
    <VueTresorerieEcran
      donnees={donnees}
      refus={refus}
      sources={sources(() => void charger())}
      fiabilite={<BandeauFiabilite jeton={jeton} ecran="tresorerie" />}
      reglages={
        <PanneauReglages
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          messageRefus={messageRefus}
          quandEnregistre={() => void charger()}
        />
      }
      actions={actions}
    />
  );
}

type Detail =
  | { sorte: 'semaine'; rang: number; nature: Nature | null }
  | { sorte: 'passe'; rang: number }
  | { sorte: 'revoir' };

export function VueTresorerieEcran({
  donnees,
  refus,
  sources,
  reglages,
  actions,
  fiabilite = null,
  panneauInitial = null,
  detailInitial = null,
}: {
  readonly donnees: TresorerieLue | null;
  readonly refus: string | null;
  readonly sources: ReactNode;
  readonly reglages: ReactNode;
  readonly actions: ActionsRegles;
  /** Le bandeau de fiabilite (lot 108). Nul dans l'apercu de maquette. */
  readonly fiabilite?: ReactNode;
  readonly panneauInitial?: 'sources' | 'reglages' | 'regles' | null;
  readonly detailInitial?: Detail | null;
}) {
  const [scenario, setScenario] = useState<Scenario>('probable');
  const [panneau, setPanneau] = useState(panneauInitial);
  const [detail, setDetail] = useState<Detail | null>(detailInitial);
  const v = useMemo(
    () => (donnees === null ? null : vueTresorerie(donnees)),
    [donnees],
  );

  if (donnees === null || v === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des comptes et des flux…</p>
        )}
      </main>
    );
  }

  const plan = scenario === 'probable' ? v.probable : v.prudent;
  const t = v.tuiles;
  const r = v.reglages;
  const bouton = (cle: 'sources' | 'reglages' | 'regles', nom: string) => (
    <button
      type="button"
      className="bouton bouton-discret bouton-petit"
      aria-expanded={panneau === cle}
      onClick={() => setPanneau(panneau === cle ? null : cle)}
    >
      {nom}
    </button>
  );
  const derniereLecture = donnees.comptes
    .map((c) => c.lu_le)
    .sort()
    .at(-1);

  return (
    <main className="corps corps-analyse">
      <div className="bfr-filtres">
        <div className="ech-champ">
          Scénario du tableau
          <div className="bfr-segments" role="group" aria-label="Scénario">
            <button
              type="button"
              aria-pressed={scenario === 'probable'}
              onClick={() => setScenario('probable')}
            >
              Probable
            </button>
            <button
              type="button"
              aria-pressed={scenario === 'prudent'}
              onClick={() => setScenario('prudent')}
            >
              Prudent
            </button>
          </div>
        </div>
        <div className="bfr-outils">
          {bouton('reglages', 'Réglages')}
          {bouton('regles', 'Rangement des mouvements')}
          {bouton('sources', 'Données Pennylane')}
        </div>
      </div>
      <p className="ech-sous">
        Plan du {dateFr(donnees.aujourdhui)} au{' '}
        {dateFr(v.semaines[v.semaines.length - 1]?.dimanche ?? donnees.aujourdhui)}
        {' · '}Solde de départ : {donnees.comptes.length} compte(s)
        {derniereLecture !== undefined && `, lus le ${heureDeParis(derniereLecture)}`}
        {' · '}Prudent : pire retard constaté des clients, sorties aux dates prévues.
        {donnees.affactures.length > 0 &&
          ` · Supposé : les transports à facturer de ${String(donnees.affactures.length)} client(s) affacturé(s) seront cédés.`}
      </p>
      {panneau === 'sources' && <div className="ech-sources">{sources}</div>}
      {panneau === 'reglages' && <div className="ech-sources">{reglages}</div>}
      {panneau === 'regles' && (
        <div className="ech-sources">
          <PanneauRegles regles={donnees.regles} actions={actions} />
        </div>
      )}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {fiabilite}
      {donnees.comptes.length === 0 && (
        <p className="bandeau bandeau-attente">
          Aucun compte bancaire lu : le solde de départ vaut 0. Lire les comptes dans «
          Données Pennylane ».
        </p>
      )}

      <div className="treso-tuiles">
        <section className="treso-tuile">
          <h3>Trésorerie du jour</h3>
          <strong>{eurosEntiers(t.solde)}</strong>
          <ul className="ech-liste">
            {donnees.comptes.map((c) => (
              <li key={c.id}>
                <span>{c.nom}</span>
                <span className="nombre">
                  {c.solde === null
                    ? 'solde non lu'
                    : eurosEntiers(Math.round(Number(c.solde) * 100))}
                </span>
              </li>
            ))}
          </ul>
        </section>
        {(['probable', 'prudent'] as const).map((sc) => {
          const bas = sc === 'probable' ? t.basProbable : t.basPrudent;
          const n = bas === null ? 'vert' : niveauDe(bas.fin, r);
          return (
            <section key={sc} className={`treso-tuile treso-${n}`}>
              <h3>Point bas {sc}</h3>
              <strong>{bas === null ? '—' : eurosEntiers(bas.fin)}</strong>
              <p>
                {bas === null
                  ? 'aucune semaine'
                  : `semaine ${String(bas.semaine.numero)}, fin le ${dateCourte(bas.semaine.dimanche)} · ${NOM_NIVEAU[n]}`}
              </p>
            </section>
          );
        })}
        <section
          className={`treso-tuile ${t.sousMinimumPrudent > 0 ? 'treso-orange' : 'treso-vert'}`}
        >
          <h3>Semaines sous le minimum</h3>
          <strong>
            {t.sousMinimumProbable} / {t.sousMinimumPrudent}
          </strong>
          <p>
            probable / prudent · minimum {eurosEntiers(r.minimum)}, découvert{' '}
            {eurosEntiers(r.decouvert)}
          </p>
        </section>
        <section
          className={`treso-tuile ${t.aRevoir.length > 0 ? 'treso-orange' : 'treso-vert'}`}
        >
          <h3>Règlements dépassés</h3>
          <strong>{t.aRevoir.length}</strong>
          <p>
            {t.aRevoir.length === 0 ? (
              'aucune date à revoir'
            ) : (
              <>
                comptés aujourd’hui (
                {eurosSignes(t.aRevoir.reduce((a, f) => a + f.montant, 0))}) ·{' '}
                <button
                  type="button"
                  className="treso-case"
                  onClick={() => setDetail({ sorte: 'revoir' })}
                >
                  voir la liste
                </button>
              </>
            )}
          </p>
        </section>
      </div>

      <section className="bfr-section">
        <h2 className="bfr-section-titre">Solde en fin de semaine</h2>
        <CourbeAnalyse
          titre="Trésorerie prévue"
          sousTitre="solde de fin de semaine · cliquer une semaine pour ses mouvements"
          etiquettes={v.semaines.map((s) => `S${String(s.numero)}`)}
          sousEtiquettes={v.semaines.map((s) => dateCourte(s.dimanche))}
          series={[
            { nom: 'probable', teinte: 1, valeurs: v.probable.map((p) => p.fin) },
            {
              nom: 'prudent',
              teinte: 4,
              valeurs: v.prudent.map((p) => p.fin),
              pointillee: true,
            },
          ]}
          seuils={[
            { valeur: r.minimum, nom: 'minimum' },
            { valeur: -r.decouvert, nom: 'découvert' },
          ]}
          courant={0}
          futurDepuis={1}
          format={eurosCourts}
          quandClic={(rang) => setDetail({ sorte: 'semaine', rang, nature: null })}
        />
      </section>

      <section className="bfr-section">
        <h2 className="bfr-section-titre">Semaine par semaine ({scenario})</h2>
        <div className="treso-cadre">
          <table className="treso-tableau">
            <thead>
              <tr>
                <th scope="col">Nature</th>
                {plan.map((p) => (
                  <th key={p.semaine.lundi} scope="col" className="nombre">
                    S{p.semaine.numero}
                    <span>{dateCourte(p.semaine.lundi)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="treso-solde">
                <th scope="row">Solde début</th>
                {plan.map((p) => (
                  <td key={p.semaine.lundi} className="nombre">
                    {eurosEntiers(p.debut)}
                  </td>
                ))}
              </tr>
              {NATURES.map((n) => (
                <tr key={n.cle}>
                  <th scope="row">{n.nom}</th>
                  {plan.map((p, i) => {
                    const m = p.parNature[n.cle];
                    return (
                      <td key={p.semaine.lundi} className="nombre">
                        {m === 0 ? (
                          <span className="ech-doux">·</span>
                        ) : (
                          <button
                            type="button"
                            className="treso-case"
                            aria-label={`${n.nom}, semaine ${String(p.semaine.numero)} : ${eurosSignes(m)}`}
                            onClick={() =>
                              setDetail({ sorte: 'semaine', rang: i, nature: n.cle })
                            }
                          >
                            {eurosSignes(m)}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="treso-solde">
                <th scope="row">Variation</th>
                {plan.map((p, i) => (
                  <td key={p.semaine.lundi} className="nombre">
                    <button
                      type="button"
                      className="treso-case"
                      onClick={() =>
                        setDetail({ sorte: 'semaine', rang: i, nature: null })
                      }
                    >
                      {eurosSignes(p.variation)}
                    </button>
                  </td>
                ))}
              </tr>
              <tr className="treso-solde">
                <th scope="row">Solde fin</th>
                {plan.map((p) => (
                  <td
                    key={p.semaine.lundi}
                    className={`nombre treso-fin treso-${niveauDe(p.fin, r)}`}
                  >
                    {eurosEntiers(p.fin)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="bfr-section">
        <h2 className="bfr-section-titre">Prévu le lundi, réalisé à la banque</h2>
        <div className="treso-cadre">
          <table className="treso-tableau treso-passe">
            <thead>
              <tr>
                <th scope="col">Semaine</th>
                <th scope="col" className="nombre">
                  Solde début
                </th>
                <th scope="col" className="nombre">
                  Variation prévue
                </th>
                <th scope="col" className="nombre">
                  Variation réelle
                </th>
                <th scope="col" className="nombre">
                  Écart
                </th>
                <th scope="col" className="nombre">
                  Solde fin
                </th>
                <th scope="col">Photo du lundi</th>
              </tr>
            </thead>
            <tbody>
              {v.passe.map((s, i) => {
                const photo = v.photos.get(s.semaine.lundi);
                return (
                  <tr key={s.semaine.lundi}>
                    <th scope="row">
                      <button
                        type="button"
                        className="treso-case"
                        onClick={() => setDetail({ sorte: 'passe', rang: i })}
                      >
                        S{s.semaine.numero} · {dateCourte(s.semaine.lundi)}
                        {s.semaine.courante && ' (en cours)'}
                      </button>
                    </th>
                    <td className="nombre">{eurosEntiers(s.debut)}</td>
                    <td className="nombre">
                      {photo === undefined ? '—' : eurosSignes(photo.variation)}
                    </td>
                    <td className="nombre">{eurosSignes(s.variation)}</td>
                    <td className="nombre">
                      {photo === undefined
                        ? '—'
                        : eurosSignes(s.variation - photo.variation)}
                    </td>
                    <td className="nombre">{eurosEntiers(s.fin)}</td>
                    <td className="ech-petit ech-doux">
                      {photo === undefined
                        ? 'pas de photo (Stafa ne l’avait pas encore)'
                        : `prise le ${heureDeParis(photo.pris_le)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="ech-sous">
          Soldes reconstruits à rebours depuis le solde du jour, avec les mouvements lus
          dans Pennylane.
          {donnees.photo_prise && ' La photo de cette semaine vient d’être prise.'}
        </p>
      </section>

      {detail !== null && (
        <Fenetre
          detail={detail}
          donnees={donnees}
          scenario={scenario}
          v={v}
          quandDetail={setDetail}
          quandFermer={() => setDetail(null)}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Les règles de rangement
// ---------------------------------------------------------------------------

export function PanneauRegles({
  regles,
  actions,
}: {
  readonly regles: readonly RegleLue[];
  readonly actions: ActionsRegles;
}) {
  const [motif, setMotif] = useState('');
  const [nature, setNature] = useState<Nature>('autres');
  const [message, setMessage] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  return (
    <section className="reglages-analyse" aria-label="Rangement des mouvements">
      <h3>Rangement des mouvements bancaires</h3>
      <p className="ech-sous">
        Un mot du libellé range le mouvement dans une nature. Vos règles passent avant
        les règles de base ; sans règle, un mouvement d’un client va en clients, d’un
        fournisseur en fournisseurs, une autre entrée en clients, une autre sortie en
        autres.
      </p>
      <form
        className="treso-regle"
        onSubmit={(e) => {
          e.preventDefault();
          setEnvoi(true);
          void actions.ajouter(motif, nature).then((m) => {
            setEnvoi(false);
            setMessage(m);
            if (m === null) setMotif('');
          });
        }}
      >
        <label className="ech-champ">
          Mot du libellé
          <input
            value={motif}
            maxLength={60}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="ex. TOTAL CARTE"
          />
        </label>
        <label className="ech-champ">
          Nature
          <select value={nature} onChange={(e) => setNature(e.target.value as Nature)}>
            {NATURES.map((n) => (
              <option key={n.cle} value={n.cle}>
                {n.nom}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="bouton bouton-petit"
          disabled={envoi || motif.trim().length < 2}
        >
          Ajouter
        </button>
        {message !== null && <span className="ech-ko">{message}</span>}
      </form>
      {regles.length === 0 ? (
        <p className="ech-vide">Aucune règle à vous pour l’instant.</p>
      ) : (
        <ul className="ech-liste">
          {regles.map((g) => (
            <li key={g.id}>
              <strong>{g.motif}</strong>
              <span>→ {nomNature(g.nature)}</span>
              <span className="ech-doux ech-petit">par {g.cree_par}</span>
              <button
                type="button"
                className="bouton bouton-discret bouton-petit prev-a-droite"
                onClick={() => void actions.retirer(g.id).then(setMessage)}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
      <details className="bfr-bloc">
        <summary>
          <span>Règles de base ({REGLES_DE_BASE.length})</span>
        </summary>
        <p className="ech-petit ech-doux">
          {REGLES_DE_BASE.map(
            (g) => `${g.motif} → ${nomNature(g.nature).split(' ')[0] ?? ''}`,
          ).join(' · ')}
        </p>
      </details>
    </section>
  );
}

// ---------------------------------------------------------------------------
// La fenêtre de détail
// ---------------------------------------------------------------------------

function ListeFlux({
  flux,
  scenario,
  tva,
}: {
  readonly flux: readonly FluxTresorerie[];
  readonly scenario: Scenario;
  readonly tva: TresorerieLue['tva'];
}) {
  return (
    <>
      {parNature(flux).map((g) => (
        <details key={g.nature} className="bfr-bloc" open>
          <summary>
            <span>
              {nomNature(g.nature)} <span className="ech-doux">({g.flux.length})</span>
            </span>
            <strong>{eurosSignes(g.total, true)}</strong>
          </summary>
          <ul className="treso-flux">
            {g.flux.map((f) => {
              const calcul =
                f.nature === 'tva'
                  ? tva.find((x) => `tva:${x.mois}` === f.cle)
                  : undefined;
              return (
                <li key={f.cle}>
                  <div className="treso-flux-tete">
                    <strong>{f.libelle}</strong>
                    <span className="nombre">{eurosSignes(f.montant, true)}</span>
                  </div>
                  <div className="ech-petit">
                    {f.tiers} · {dateFr(scenario === 'prudent' ? f.prudent : f.date)}
                    {f.prudent !== f.date &&
                      (scenario === 'prudent'
                        ? ` (probable ${dateCourte(f.date)})`
                        : ` (prudent ${dateCourte(f.prudent)})`)}
                    {f.a_revoir && <span className="ech-etiquette">à revoir</span>}
                  </div>
                  <div className="ech-petit ech-doux">
                    {f.source} · {f.calcul}
                  </div>
                  {calcul !== undefined && (
                    <table className="treso-tva">
                      <tbody>
                        {calcul.lignes.map((l) => (
                          <tr key={l.libelle}>
                            <td>{l.libelle}</td>
                            <td className="nombre">
                              {l.collectee === 0 ? '' : eurosSignes(l.collectee, true)}
                            </td>
                            <td className="nombre">
                              {l.deductible === 0
                                ? ''
                                : eurosSignes(-l.deductible, true)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <th scope="row">À reverser le {dateFr(calcul.paiement)}</th>
                          <td className="nombre" colSpan={2}>
                            <strong>
                              {eurosSignes(calcul.net, true).replace('+', '')}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </>
  );
}

function Fenetre({
  detail,
  donnees,
  scenario,
  v,
  quandDetail,
  quandFermer,
}: {
  readonly detail: Detail;
  readonly donnees: TresorerieLue;
  readonly scenario: Scenario;
  readonly v: ReturnType<typeof vueTresorerie>;
  readonly quandDetail: (d: Detail) => void;
  readonly quandFermer: () => void;
}) {
  const nb =
    detail.sorte === 'semaine'
      ? v.semaines.length
      : detail.sorte === 'passe'
        ? v.passe.length
        : 0;
  const rang = detail.sorte === 'revoir' ? 0 : detail.rang;
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
      if (detail.sorte === 'revoir') return;
      if (e.key === 'ArrowLeft' && rang > 0) quandDetail({ ...detail, rang: rang - 1 });
      if (e.key === 'ArrowRight' && rang < nb - 1)
        quandDetail({ ...detail, rang: rang + 1 });
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [detail, rang, nb, quandDetail, quandFermer]);

  let titre = '';
  let sous = '';
  let corps: ReactNode = null;
  if (detail.sorte === 'revoir') {
    titre = 'Règlements dépassés, comptés aujourd’hui';
    sous = 'Leur date prévue est passée : à mettre à jour dans l’échéancier.';
    corps = <ListeFlux flux={v.tuiles.aRevoir} scenario="probable" tva={donnees.tva} />;
  } else if (detail.sorte === 'semaine') {
    const s = v.semaines[rang];
    const p = (scenario === 'probable' ? v.probable : v.prudent)[rang];
    if (s !== undefined && p !== undefined) {
      const tous = fluxDeLaSemaine(donnees.flux, s, scenario, donnees.aujourdhui);
      const flux =
        detail.nature === null ? tous : tous.filter((f) => f.nature === detail.nature);
      const total = flux.reduce((a, f) => a + f.montant, 0);
      titre =
        detail.nature === null
          ? `Semaine ${String(s.numero)}`
          : `${nomNature(detail.nature)} · semaine ${String(s.numero)}`;
      sous = `du ${dateFr(s.lundi < donnees.aujourdhui ? donnees.aujourdhui : s.lundi)} au ${dateFr(s.dimanche)} · scénario ${scenario}`;
      corps = (
        <>
          <p className="bfr-calcul">
            {detail.nature === null ? (
              <>
                {eurosEntiers(p.debut)} {eurosSignes(p.entrees)} (entrées){' '}
                {eurosSignes(p.sorties)} (sorties) ={' '}
                <strong>{eurosEntiers(p.fin)}</strong> ·{' '}
                {NOM_NIVEAU[niveauDe(p.fin, v.reglages)]}
              </>
            ) : (
              <>
                {flux.length} mouvement(s) = <strong>{eurosSignes(total, true)}</strong>
              </>
            )}
          </p>
          {flux.length === 0 ? (
            <p className="ech-vide">Aucun mouvement prévu.</p>
          ) : (
            <ListeFlux flux={flux} scenario={scenario} tva={donnees.tva} />
          )}
        </>
      );
    }
  } else {
    const s = v.passe[rang];
    if (s !== undefined) {
      const photo = v.photos.get(s.semaine.lundi);
      titre = `Semaine ${String(s.semaine.numero)} · réalisé`;
      sous = `du ${dateFr(s.semaine.lundi)} au ${dateFr(s.semaine.arrete)}`;
      corps = (
        <>
          <p className="bfr-calcul">
            {eurosEntiers(s.debut)} {eurosSignes(s.variation)} ={' '}
            <strong>{eurosEntiers(s.fin)}</strong>
            {photo !== undefined &&
              ` · prévu le lundi : ${eurosSignes(photo.variation)} (photo du ${heureDeParis(photo.pris_le)})`}
          </p>
          <div className="ech-tableau">
            <table>
              <thead>
                <tr>
                  <th>Nature</th>
                  <th className="nombre">Prévu</th>
                  <th className="nombre">Réel</th>
                  <th className="nombre">Écart</th>
                </tr>
              </thead>
              <tbody>
                {NATURES.map((n) => {
                  const prevu = photo?.parNature[n.cle];
                  const reel = s.parNature[n.cle];
                  return (
                    <tr key={n.cle}>
                      <td>{n.nom}</td>
                      <td className="nombre">
                        {prevu === undefined ? '—' : eurosSignes(prevu)}
                      </td>
                      <td className="nombre">{eurosSignes(reel)}</td>
                      <td className="nombre">
                        {prevu === undefined ? '—' : eurosSignes(reel - prevu)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <details className="bfr-bloc" open>
            <summary>
              <span>
                Mouvements bancaires{' '}
                <span className="ech-doux">({s.mouvements.length})</span>
              </span>
              <strong>{eurosSignes(s.variation, true)}</strong>
            </summary>
            <ul className="treso-flux">
              {s.mouvements.map((m) => (
                <li key={m.cle}>
                  <div className="treso-flux-tete">
                    <strong>{m.libelle}</strong>
                    <span className="nombre">{eurosSignes(m.montant, true)}</span>
                  </div>
                  <div className="ech-petit ech-doux">
                    {dateFr(m.date)} · {m.compte} · {nomNature(m.nature)}
                    {m.regle === null ? ' (par défaut)' : ` (règle « ${m.regle} »)`}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        </>
      );
    }
  }

  return (
    <div className="ech-voile bfr-voile" role="presentation" onClick={quandFermer}>
      <div
        className="bfr-fenetre"
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{titre}</h2>
            <p className="ech-sous">{sous}</p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>
        {detail.sorte !== 'revoir' && (
          <div className="ech-actions">
            <button
              type="button"
              className="bouton bouton-discret bouton-petit"
              disabled={rang === 0}
              onClick={() => quandDetail({ ...detail, rang: rang - 1 })}
            >
              ◂ Semaine précédente
            </button>
            <button
              type="button"
              className="bouton bouton-discret bouton-petit"
              disabled={rang >= nb - 1}
              onClick={() => quandDetail({ ...detail, rang: rang + 1 })}
            >
              Semaine suivante ▸
            </button>
          </div>
        )}
        {corps}
      </div>
    </div>
  );
}
